/**
 * Observador passivo das respostas da instância `api` (spec OBS-12 a OBS-18).
 *
 * Fica aqui, e não em `services/interceptors.ts`, para a telemetria não encostar na camada de
 * requisição: os interceptadores de resposta rodam na ordem de registro, então este roda depois do
 * existente (renovação de token incluída) e só lê o que já aconteceu. Devolve a mesma resposta e
 * rejeita com o mesmo erro; qualquer falha da telemetria é engolida antes de chegar à tela.
 */
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { ApiResult, classifyFlowStep, FlowStepAttributes } from './businessFlows';

export const FLOW_STEP_EVENT = 'jogajunto.fluxo';

export type PushFlowEvent = (name: string, attributes: FlowStepAttributes) => void;

type Settled = { response?: AxiosResponse; error?: AxiosError };

/** Tudo dentro do `try`: nem um objeto inesperado do interceptador anterior pode virar erro na tela. */
function report(pushEvent: PushFlowEvent, { response, error }: Settled): void {
  try {
    const config: AxiosRequestConfig | undefined = response ? response.config : error?.config;
    if (!config?.url || !config.method) return;

    const settled = response ?? error?.response;
    const result: ApiResult = { httpStatus: response ? response.status : (settled?.status ?? 0), responseData: settled?.data };
    const attributes = classifyFlowStep(
      { method: config.method, url: config.url, body: config.data, pagePath: window.location.pathname },
      result,
    );
    if (attributes) pushEvent(FLOW_STEP_EVENT, attributes);
  } catch {
    // Telemetria nunca interfere na chamada.
  }
}

export function observeApiFlows(apiInstance: AxiosInstance, pushEvent: PushFlowEvent): void {
  apiInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      report(pushEvent, { response });
      return response;
    },
    (error: AxiosError) => {
      report(pushEvent, { error });
      return Promise.reject(error);
    },
  );
}
